import { useTheme } from "@/hooks/useTheme";

export default function Footer() {
  const { isDark } = useTheme();

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const bg = isDark ? "#15131A" : "#F0E9DE";
  const borderColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(28,26,34,0.1)";
  const textMuted = isDark ? "rgba(255,255,255,0.3)" : "rgba(28,26,34,0.4)";
  const textHover = isDark ? "rgba(255,255,255,0.7)" : "rgba(28,26,34,0.8)";
  const headingColor = isDark ? "rgba(255,255,255,0.6)" : "rgba(28,26,34,0.6)";
  const iconBorder = isDark ? "rgba(255,255,255,0.08)" : "rgba(28,26,34,0.1)";
  const iconColor = isDark ? "rgba(255,255,255,0.3)" : "rgba(28,26,34,0.35)";
  const bottomBorder = isDark ? "rgba(255,255,255,0.05)" : "rgba(28,26,34,0.07)";
  const copyColor = isDark ? "rgba(255,255,255,0.2)" : "rgba(28,26,34,0.3)";

  return (
    <footer
      className="relative transition-colors duration-500"
      style={{ background: bg, borderTop: `1px solid ${borderColor}` }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">
          <div className="lg:col-span-2">
            <button onClick={() => scrollTo("home")} className="cursor-pointer mb-5 block">
              <img
                src="https://public.readdy.ai/ai/img_res/abee19c3-0782-4d36-ae83-20a2d803b3c1.png"
                alt="Automation Integration AI"
                className="h-12 w-auto object-contain transition-all duration-500"
                style={{
                  filter: isDark
                    ? "drop-shadow(0 0 10px rgba(232,118,26,0.2))"
                    : "drop-shadow(0 0 6px rgba(232,118,26,0.15)) brightness(0.9)",
                }}
              />
            </button>
            <p className="text-sm leading-relaxed max-w-xs mb-6 font-light transition-colors duration-500" style={{ color: textMuted }}>
              Where Systems Connect &amp; Revenue Flows. We turn your operations into a fully connected system.
            </p>
            <div className="flex items-center gap-3">
              {[
                { icon: "ri-linkedin-box-line", href: "#" },
                { icon: "ri-twitter-x-line", href: "#" },
                { icon: "ri-youtube-line", href: "#" },
              ].map((s) => (
                <a
                  key={s.icon}
                  href={s.href}
                  rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 cursor-pointer"
                  style={{ border: `1px solid ${iconBorder}`, color: iconColor }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.color = "#E8761A";
                    (e.currentTarget as HTMLElement).style.borderColor = "rgba(232,118,26,0.3)";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.color = iconColor;
                    (e.currentTarget as HTMLElement).style.borderColor = iconBorder;
                  }}
                >
                  <i className={s.icon}></i>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h5 className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-5 transition-colors duration-500" style={{ color: headingColor }}>Solutions</h5>
            <ul className="space-y-3">
              {["AI Call Handling", "Smart Scheduling", "CRM Integration", "Automated Follow-ups", "Centralized Comms"].map((item) => (
                <li key={item}>
                  <button
                    onClick={() => scrollTo("home")}
                    className="text-sm transition-colors cursor-pointer text-left font-light"
                    style={{ color: textMuted }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = textHover)}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = textMuted)}
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h5 className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-5 transition-colors duration-500" style={{ color: headingColor }}>Industries</h5>
            <ul className="space-y-3">
              {["Home Healthcare", "Home Services", "Golf Courses", "Restaurants", "Legal Services", "Financial Services"].map((item) => (
                <li key={item}>
                  <button
                    onClick={() => scrollTo("industries")}
                    className="text-sm transition-colors cursor-pointer text-left font-light"
                    style={{ color: textMuted }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = textHover)}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = textMuted)}
                  >
                    {item}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div
          className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ borderTop: `1px solid ${bottomBorder}` }}
        >
          <p className="text-xs font-light transition-colors duration-500" style={{ color: copyColor }}>
            &copy; {new Date().getFullYear()} Automation Integration AI. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {["Privacy Policy", "Terms of Service"].map((label) => (
              <a
                key={label}
                href="#"
                rel="nofollow"
                className="text-xs transition-colors cursor-pointer font-light"
                style={{ color: copyColor }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = textMuted)}
                onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = copyColor)}
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
